// Detalle de un chofer (Driver) dentro de Liquidaciones > Choferes: mismo
// patrón que ClienteDetailModal.tsx, entidad Driver en vez de Store.
// Reutiliza el mismo OverrideEditModal (Turbo/×2 Doble/Precio manual/CP) —
// la edición financiera de un envío es idéntica, solo cambia a qué
// endpoint se manda.
//
// Driver Daily Performance / Premio Elite (ver informe "LYNX — Driver
// Daily Performance + Premios Elite"): el detalle ahora se organiza por
// DÍA (Resumen + Viernes..Jueves) en vez de una lista plana — cada
// pestaña de día muestra únicamente los envíos de ese día, con
// Dirección/Zona/Base/Premio/Total/Estado del Premio, y el pie de página
// con los totales de ese día. La pestaña Resumen agrega Total Semana,
// Total Premios y Total General.
import React, { useEffect, useMemo, useState } from "react";
import Card from "../ui/Card";
import Button from "../ui/Button";
import Input from "../ui/Input";
import { authFetch } from "../../lib/authFetch";
import { downloadAuthenticatedFile } from "../../lib/downloadFile";
import OverrideEditModal, { DeliveryDetail, money, OptionCard } from "./OverrideEditModal";
import EnvioLegacyDetailModal from "../envios/EnvioLegacyDetailModal";

type ConfigStatus = "CONFIGURADO" | "CON_PENDIENTES" | "SIN_TARIFA";

type DriverSummary = {
  deliveries: number;
  pricedDeliveries: number;
  unresolvedDeliveries: number;
  totalReparto: number;
  premioElite: number | null;
  colecta: number | null;
  totalFinal: number;
};

type DayTotals = {
  deliveries: number;
  pricedDeliveries: number;
  base: number;
  premios: number;
  total: number;
};

type DriverDay = {
  dateKey: string;
  dayLabel: string; // "Viernes 18/09/2026"
  closed: boolean; // false únicamente para HOY — todavía puede cambiar
  deliveries: DeliveryDetail[];
  // Veredicto Elite del día completo (ver informe "LYNX — Driver Daily
  // Performance + Premios Elite", ajuste "Resumen diario"): null si ese día
  // no tuvo ningún envío elite-elegible en juego (nunca "ganó" ni "perdió").
  eliteStatus: "GANADO" | "PERDIDO" | null;
  eliteReason: string | null;
  // Causantes reales de la pérdida global (ver auditoría "LYNX — Aura Flex
  // / Elite individual vs pérdida global") — puede haber más de uno, nunca
  // solo el primero encontrado.
  eliteCausantes: { lightdata_id: string | number; tracking: string | null; store_name: string | null; reason: string | null; estado: string | null; fecha_estado: string | null }[];
  totals: DayTotals;
};

// Settlement Adjustments (ver informe "LYNX — Ajustes manuales sobre
// semanas cerradas"): capa de correcciones POR ENCIMA del snapshot
// inmutable, exclusiva de semanas ya CERRADAS — nunca reescribe
// driver_daily_snapshot. Vehículo = alcance semana completa (re-tarifa);
// Premio Elite = alcance UN día puntual (mismo criterio que las pestañas
// de día que ya existen acá).
type VehiculoAdjustment = { value: string; reason: string; updatedAt: string };
type EliteAdjustment = { direction: "GANADO" | "PERDIDO"; reason: string; updatedAt: string };
type AdjustmentsSummary = {
  hasAny: boolean;
  vehiculo: VehiculoAdjustment | null;
  eliteByDay: Record<string, EliteAdjustment>;
};

type AdjustmentHistoryEntry = {
  id: number;
  field: "VEHICULO" | "ELITE_OVERRIDE";
  scopeDate: string | null;
  previousValue: string | null;
  newValue: string;
  reason: string;
  createdAt: string;
  createdBy: { id: number | string; username: string | null; name: string | null };
};

type DriverDetailResponse = {
  ok: boolean;
  // readOnly (ver informe "Sprint Final — Cierre semanal", Parte 4): true en
  // una semana ya CERRADA — mismo patrón que ClienteDetailModal.tsx. Es
  // independiente de `day.closed` (un día puede estar cerrado para Premio
  // Elite mientras la semana sigue abierta y los overrides siguen andando).
  period: { key: string; fromDisplay: string; toDisplay: string; readOnly?: boolean };
  driver: {
    id: number;
    name: string | null;
    lightdataUsername: string | null;
    vehiculo: string | null;
    tariff: { id: number; name: string } | null;
    configStatus: ConfigStatus | null;
    availableTariffs: { id: number; name: string }[] | null;
  };
  summary: DriverSummary;
  days: DriverDay[];
  adjustments: AdjustmentsSummary | null;
};

const CONFIG_BADGE: Record<ConfigStatus, { label: string; color: string }> = {
  CONFIGURADO: { label: "Configurado", color: "var(--success, #2ea043)" },
  CON_PENDIENTES: { label: "Con pendientes", color: "#c99a1f" },
  SIN_TARIFA: { label: "Sin tarifa", color: "var(--danger, #d1242f)" },
};

// ELITE_STATUS_LABEL: ya NO se usa como texto visible principal (ver "LYNX
// — Cierre definitivo Elite/Snapshot 06/10", punto 5-6 — "No elegible"/
// "No pagable" quedan eliminados como explicación visible). Se conserva
// solo para AWARDED/LOST/UNPRICED como fallback interno de muy bajo
// nivel — el texto real que ve el usuario sale siempre de
// categorizeEliteReason/nonFlexLabel/TURBO_LABEL más abajo.
const ELITE_STATUS_LABEL: Record<string, { label: string; color: string }> = {
  AWARDED: { label: "Ganado", color: "var(--success, #2ea043)" },
  LOST: { label: "Perdido", color: "var(--danger, #d1242f)" },
  UNPRICED: { label: "Sin precio", color: "var(--text-secondary)" },
};

const TURBO_LABEL: EliteCategoryLike = { label: "Envío Turbo — fuera de medición Elite", icon: "⚡", color: "var(--text-secondary)" };

// nonFlexLabel (ver punto 4-6): "Envío no Flex — <origen>" — nunca "No
// elegible". origen tal cual lo manda el backend (Directo/TN/Shopify/Woo/
// lo que corresponda); "Tienda Nube" es el nombre comercial real de "TN"
// para el usuario, no un origen nuevo.
const ORIGEN_DISPLAY: Record<string, string> = { TN: "Tienda Nube", Woo: "WooCommerce" };
function nonFlexLabel(origen: string | null | undefined): EliteCategoryLike {
  const display = (origen && ORIGEN_DISPLAY[origen]) || origen || "origen desconocido";
  return { label: `Envío no Flex — ${display}`, icon: "🏷️", color: "var(--text-secondary)" };
}

// Categorización de `elite_reason` para presentación (ver auditoría Fase
// 1.7 — "Elite UX", actualizada en "LYNX — Cierre definitivo Elite/
// Snapshot 06/10", punto 5-6): el motor Elite (eliteDecision.service.js/
// REASON) ya devuelve oraciones completas en español — acá SOLO se las
// mapea a la taxonomía visible aprobada, nunca se reinterpreta ni
// recalcula la decisión. Nadie/Reprogramado YA NO distinguen 21-23 de
// post-23 (el motor dejó de escalar por hora — ver eliteDecision.service.js)
// pero el match sigue tolerando el texto viejo "después de las 23:00" para
// no romper la lectura de Snapshots ya congelados con el motor anterior.
type EliteCategoryLike = { label: string; icon: string; color: string };
type EliteCategory = EliteCategoryLike;

function categorizeEliteReason(reason: string | null): EliteCategory | null {
  if (!reason) return null;
  if (reason === "Entregado antes de las 21:00") return { label: "Elite — Entregado antes de las 21:00", icon: "⭐", color: "var(--success, #2ea043)" };
  if (reason === "Entregado entre las 21:00 y las 22:59") return { label: "Bien — Entregado entre 21:00 y 22:59", icon: "🟢", color: "var(--text-secondary)" };
  if (reason === "Nadie en el domicilio antes de las 21:00") return { label: "Bien — Nadie antes de las 21:00", icon: "🟢", color: "var(--text-secondary)" };
  if (reason.startsWith("Nadie en el domicilio")) return { label: "Regular — Cerrado como Nadie post 21", icon: "🚪", color: "#c99a1f" };
  if (reason === "Reprogramado antes de las 21:00") return { label: "Bien — Reprogramado antes de las 21:00", icon: "🟢", color: "var(--text-secondary)" };
  if (reason.startsWith("Reprogramado")) return { label: "Regular — Envío reprogramado post 21", icon: "📅", color: "#c99a1f" };
  if (reason === "Entregado después de las 23:00") return { label: "Demorado — Entregado después de las 23:00", icon: "⏱️", color: "var(--danger, #d1242f)" };
  if (reason.startsWith("En camino")) return { label: "Demorado — En camino a las 23:00", icon: "🚚", color: "var(--danger, #d1242f)" };
  if (reason.startsWith("Demorado")) return { label: "Demorado", icon: "⏱️", color: "var(--danger, #d1242f)" };
  if (reason.startsWith("Más de")) return { label: "Demora — demasiados envíos cerrados como Nadie/Reprogramado post 21", icon: "⚠️", color: "var(--danger, #d1242f)" };
  if (reason.startsWith("Estado no clasificado")) return { label: "No clasificado", icon: "❓", color: "var(--text-secondary)" };
  if (reason === "Envío cancelado") return { label: "Cancelado", icon: "🚫", color: "var(--text-secondary)" };
  if (reason === "Envío rechazado") return { label: "Rechazado", icon: "🚫", color: "var(--text-secondary)" };
  if (reason.startsWith("Sin hora")) return { label: "Sin hora válida", icon: "❓", color: "var(--text-secondary)" };
  // Fallback honesto: cualquier motivo no mapeado se muestra tal cual lo
  // devuelve el backend, nunca se inventa una categoría nueva.
  return { label: reason, icon: "•", color: "var(--text-secondary)" };
}

// Contadores del día (ver auditoría "LYNX — Aura Flex / Elite individual vs
// pérdida global", actualizado en "LYNX — Cierre definitivo Elite/Snapshot
// 06/10", punto 6E/7): agrupación PURA de presentación sobre
// `elite_status`/`elite_reason`/`elite_is_causante`/`elite_is_late_exception`
// que el backend YA mandó por envío — nunca reconstruye ninguna regla de
// negocio, solo cuenta lo que ya fue decidido. `lostButSuccessful` es la
// verdad que antes se perdía: envíos que individualmente salieron bien
// (LOST económicamente, pero NO causantes). `lateExceptionCount` (nuevo):
// cuántos Nadie/Reprogramado post21 hubo — estas filas YA NUNCA tienen
// `elite_is_causante=true` (el motor dejó de escalarlas a descalificador
// duro por hora), así que cuando el día se pierde por CANTIDAD
// (`day.reason` empieza con "Más de"), `causantes` queda vacío aunque el
// día SÍ esté perdido — por eso el caller agrega la explicación agregada
// "Demora — demasiados..." usando este conteo, nunca convirtiendo esas
// filas individuales en "causantes".
export function buildDayEliteCounts(deliveries: DeliveryDetail[]): {
  elite: number;
  lostButSuccessful: number;
  causantes: { label: string; icon: string; color: string; count: number }[];
  lateExceptionCount: number;
  rejectedCount: number;
} {
  let elite = 0;
  let lostButSuccessful = 0;
  let lateExceptionCount = 0;
  // Rechazados (ver "LYNX — FASE 4: Elite visual"): cuenta elite_reason
  // === "Envío rechazado" (REASON.REJECTED, eliteDecision.service.js) tal
  // cual lo devuelve el motor -- nunca reinterpreta ni recalcula. Son
  // NOT_ELIGIBLE (no candidatos a Elite), por eso antes no aparecían en
  // ningún contador de este bloque.
  let rejectedCount = 0;
  const causanteMap = new Map<string, { icon: string; color: string; count: number }>();
  for (const d of deliveries) {
    if (d.elite_is_late_exception) lateExceptionCount += 1;
    if (d.elite_reason === "Envío rechazado") rejectedCount += 1;
    if (d.elite_status === "AWARDED") {
      elite += 1;
      continue;
    }
    if (d.elite_is_causante) {
      const cat = categorizeEliteReason(d.elite_reason) || { label: "Otro motivo", icon: "•", color: "var(--text-secondary)" };
      const existing = causanteMap.get(cat.label);
      if (existing) existing.count += 1;
      else causanteMap.set(cat.label, { icon: cat.icon, color: cat.color, count: 1 });
      continue;
    }
    if (d.elite_status === "LOST") {
      lostButSuccessful += 1;
    }
  }
  const causantes = [...causanteMap.entries()].map(([label, v]) => ({ label, ...v }));
  return { elite, lostButSuccessful, causantes, lateExceptionCount, rejectedCount };
}

type Props = {
  open: boolean;
  driverId: number | string | null;
  driverName: string | null;
  token: string | null;
  onClose: () => void;
  // ausente = semana abierta actual; con valor, abre esa semana histórica
  // (ver informe "Sprint Final — Cierre semanal", Parte 3/4).
  periodKey?: string | null;
  // Se llama después de guardar/quitar un override, para que la pantalla
  // que lista todos los choferes también refresque su total.
  onOverrideChanged?: () => void;
};

const overlayStyle: React.CSSProperties = { position: "fixed", inset: 0, zIndex: 120, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" };
const backdropStyle: React.CSSProperties = { position: "absolute", inset: 0, background: "rgba(0,0,0,0.65)" };
const shellStyle: React.CSSProperties = { position: "relative", width: "100%", maxWidth: "min(96vw, 1600px)", maxHeight: "90vh", overflow: "hidden" };
const headerStyle: React.CSSProperties = { display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", padding: "18px 20px", borderBottom: "1px solid var(--border)" };
const titleStyle: React.CSSProperties = { margin: 0, fontSize: "18px", fontWeight: 700, color: "var(--text-primary)" };
const subtitleStyle: React.CSSProperties = { margin: "4px 0 0 0", fontSize: "13px", color: "var(--text-secondary)" };
const bodyStyle: React.CSSProperties = { padding: "20px", maxHeight: "76vh", overflowY: "auto", display: "flex", flexDirection: "column", gap: "14px" };
const stateStyle: React.CSSProperties = { padding: "24px 0", color: "var(--text-secondary)", fontSize: "14px", textAlign: "center" };
const tableWrapStyle: React.CSSProperties = { overflowX: "auto", border: "1px solid var(--border)", borderRadius: "14px" };
// Tabla principal del modal ancho (Resumen / envíos del día): a partir de
// 1180px (mismo breakpoint que ya usa el resto de la app para pasar de
// grilla ancha a angosta, ver Deliveries.tsx/HomeAdmin.tsx/etc.) la tabla
// fluye libre sin scroll horizontal, porque el modal ya tiene ancho de
// sobra (shellStyle = min(96vw, 1600px)); por debajo de eso, overflow-x:
// auto queda como fallback para no romper pantallas angostas.
const wideTableWrapStyle: React.CSSProperties = { border: "1px solid var(--border)", borderRadius: "14px" };
const tableStyle: React.CSSProperties = { width: "100%", borderCollapse: "collapse" };
const thStyle: React.CSSProperties = { textAlign: "left", padding: "10px", color: "var(--text-secondary)", fontSize: "12px", fontWeight: 700, borderBottom: "1px solid var(--border)", whiteSpace: "nowrap", background: "rgba(255,255,255,0.02)" };
const tdStyle: React.CSSProperties = { padding: "8px 10px", color: "var(--text-primary)", fontSize: "13px", borderBottom: "1px solid var(--border)", whiteSpace: "nowrap" };
const badgeStyle = (color: string): React.CSSProperties => ({ display: "inline-block", padding: "2px 9px", borderRadius: "999px", fontSize: "11px", fontWeight: 700, border: `1px solid ${color}`, color, marginRight: "4px" });
const editButtonStyle: React.CSSProperties = { background: "transparent", border: "1px solid var(--border)", borderRadius: "8px", width: "28px", height: "28px", cursor: "pointer", fontSize: "13px", color: "var(--text-secondary)" };
const searchFieldStyle: React.CSSProperties = { maxWidth: "360px" };
const tabBarStyle: React.CSSProperties = { display: "flex", gap: "6px", flexWrap: "wrap", borderBottom: "1px solid var(--border)", paddingBottom: "10px" };
const tabBtnStyle = (active: boolean): React.CSSProperties => ({
  padding: "7px 14px",
  borderRadius: "10px",
  border: `1px solid ${active ? "var(--primary)" : "var(--border)"}`,
  background: active ? "var(--primary-soft)" : "transparent",
  color: active ? "var(--primary)" : "var(--text-secondary)",
  fontSize: "13px",
  fontWeight: 700,
  cursor: "pointer",
});
const summaryCardStyle: React.CSSProperties = { padding: "14px 16px", borderRadius: "14px", border: "1px solid var(--border)", background: "var(--surface-alt, transparent)" };
const summaryLabelStyle: React.CSSProperties = { fontSize: "11px", color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: "0.04em" };
const summaryValueStyle: React.CSSProperties = { fontSize: "14px", fontWeight: 600 };
const adjustmentsBannerStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "12px",
  flexWrap: "wrap",
  padding: "10px 14px",
  borderRadius: "12px",
  border: "1px solid #c99a1f",
  background: "rgba(201,154,31,0.1)",
  color: "#c99a1f",
  fontSize: "13px",
  fontWeight: 700,
};
const adjustmentsSectionStyle: React.CSSProperties = {
  padding: "14px 16px",
  borderRadius: "14px",
  border: "1px dashed var(--border)",
  display: "flex",
  flexDirection: "column",
  gap: "10px",
};
const adjustmentsTitleStyle: React.CSSProperties = { margin: 0, fontSize: "13px", fontWeight: 700, color: "var(--text-primary)" };
const currentAdjustmentNoteStyle: React.CSSProperties = { fontSize: "12px", color: "var(--text-secondary)" };
const historyOverlayStyle: React.CSSProperties = { position: "fixed", inset: 0, zIndex: 160, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" };
const historyBackdropStyle: React.CSSProperties = { position: "absolute", inset: 0, background: "rgba(0,0,0,0.65)" };

// Nombre corto del día para la pestaña ("Viernes 18/09/2026" -> "Viernes").
function shortDayLabel(dayLabel: string): string {
  return dayLabel.split(" ")[0];
}

// Orden de presentación Flex -> Flex Turbo -> Tienda Nube -> Directo ->
// Otros (ver "LYNX — Correcciones finales antes de cerrar FASE B",
// sección 3): SOLO dentro de este modal + el Excel del Driver —
// explícitamente retractado para Lista Legacy. Campos reales existentes
// (origen/is_turbo), nunca inferidos de texto/estado. Dentro de cada
// balde se conserva el orden que el backend ya trae (sort estable) —
// nunca toca cálculos, pricing, Elite ni totales, es puramente visual.
export function originBucket(d: DeliveryDetail): number {
  if (d.origen === "MercadoLibre" && !d.is_turbo) return 0; // Flex
  if (d.origen === "MercadoLibre" && d.is_turbo) return 1; // Flex Turbo
  if (d.origen === "TN") return 2; // Tienda Nube
  if (d.origen === "Directo") return 3; // Directo
  return 4; // Otros — siempre último
}
export function sortByOriginBucket(deliveries: DeliveryDetail[]): DeliveryDetail[] {
  return [...deliveries].sort((a, b) => originBucket(a) - originBucket(b));
}

// Hora de pared Argentina de un fecha_estado crudo, para el bloque de
// causantes ("Entregado 23:14 — Entregado después de las 23:00") — vía
// Intl con timeZone explícito, igual que el backend (nunca -3hs fijo ni
// dependiente del navegador del usuario).
function argentinaHour(fechaEstado: string | null): string | null {
  if (!fechaEstado) return null;
  const d = new Date(fechaEstado);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleTimeString("es-AR", { timeZone: "America/Argentina/Buenos_Aires", hour: "2-digit", minute: "2-digit" });
}

// Contadores del día (ver auditoría "LYNX — Aura Flex / Elite individual vs
// pérdida global"): responde en un vistazo "cuántos Elite, cuántos exitosos
// sin premio por pérdida global, cuántos causantes de qué" — pura
// presentación sobre buildDayEliteCounts. "Exitosos sin premio" es la
// distinción nueva: antes esos envíos aparecían mezclados con los
// causantes reales en el mismo balde (ej. "Reprogramado (post 23hs): 63"
// aunque 62 de esos nunca estuvieron reprogramados).
// dayEliteReason (ver "LYNX — Cierre definitivo Elite/Snapshot 06/10",
// punto 6E): el motivo del DÍA (activeDay.eliteReason) — cuando empieza
// con "Más de" (REASON.LATE_EXCEPTION_COUNT), el día se perdió por
// CANTIDAD de excepciones Nadie/Reprogramado, nunca por un hardDisqualifier
// individual — causantes queda vacío en ese caso (confirmado en
// eliteDecision.service.js), así que la explicación agregada se muestra
// acá, usando lateExceptionCount, sin convertir ninguna fila individual en
// "causante".
function DayEliteCounters({ deliveries, dayEliteReason }: { deliveries: DeliveryDetail[]; dayEliteReason?: string | null }) {
  const { elite, lostButSuccessful, causantes, lateExceptionCount, rejectedCount } = useMemo(() => buildDayEliteCounts(deliveries), [deliveries]);
  const showAggregateException = lateExceptionCount > 0 && causantes.length === 0 && !!dayEliteReason && dayEliteReason.startsWith("Más de");
  if (elite === 0 && lostButSuccessful === 0 && causantes.length === 0 && rejectedCount === 0 && !showAggregateException) return null;
  return (
    <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
      {elite > 0 && (
        <span style={badgeStyle("var(--success, #2ea043)")}>⭐ Elite: {elite}</span>
      )}
      {lostButSuccessful > 0 && (
        <span style={badgeStyle("var(--success, #2ea043)")} title="Entregados a tiempo, pero sin premio porque otro envío hizo perder el día completo">
          🟢 Exitosos sin premio (pérdida global): {lostButSuccessful}
        </span>
      )}
      {showAggregateException && (
        <span style={badgeStyle("var(--danger, #d1242f)")} title="Ninguna de estas excepciones es individualmente causante — lo es el conjunto (más de 2 el mismo día)">
          🔴 ⚠️ Demora — demasiados envíos cerrados como Nadie/Reprogramado post 21 ({lateExceptionCount} {">"} 2)
        </span>
      )}
      {causantes.map((l) => (
        <span key={l.label} style={badgeStyle(l.color)} title="Causante de la pérdida global del día">
          🔴 {l.icon} {l.label}: {l.count}
        </span>
      ))}
      {rejectedCount > 0 && (
        <span style={badgeStyle("var(--text-secondary)")} title="Envíos rechazados — no son candidatos a Premio Elite, no penalizan el día">
          ⚠️ Rechazados: {rejectedCount}
        </span>
      )}
    </div>
  );
}

// Badges de excepción: ausencia de badge = cálculo normal, nunca se rotula "NORMAL".
function ExceptionBadges({ d }: { d: DeliveryDetail }) {
  return (
    <>
      {d.is_turbo && <span style={badgeStyle(d.turbo_source === "MANUAL" ? "#c99a1f" : "#1f7ac9")}>⚡ Turbo</span>}
      {d.is_double && <span style={badgeStyle("#1f9c7a")}>×2 Doble</span>}
      {d.manual_price !== null && <span style={badgeStyle("#8a3fd1")}>✏️ Manual</span>}
    </>
  );
}

const ADJUSTMENT_FIELD_LABEL: Record<string, string> = { VEHICULO: "Vehículo", ELITE_OVERRIDE: "Premio Elite" };

function formatHistoryDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

// Caso 3 del informe "LYNX — Ajustes manuales sobre semanas cerradas":
// historial completo (usuario/fecha/campo/valor anterior/valor nuevo/
// motivo), append-only — nunca se edita ni se borra una fila, así que esto
// es de solo lectura.
function AdjustmentHistoryModal({ open, driverId, periodKey, token, onClose }: { open: boolean; driverId: number | string | null; periodKey: string | null; token: string | null; onClose: () => void }) {
  const [history, setHistory] = useState<AdjustmentHistoryEntry[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || driverId === null || !periodKey || !token) return;
    setLoading(true);
    setError(null);
    authFetch(`/v1/liquidaciones/choferes/${driverId}/adjustments/history?period=${encodeURIComponent(periodKey)}`, { token })
      .then(async (res) => {
        const payload = await res.json().catch(() => ({}));
        if (!res.ok || payload?.ok === false) throw new Error(payload?.error || "Error al cargar el historial de ajustes");
        setHistory(payload.history);
      })
      .catch((e: any) => setError(e.message))
      .finally(() => setLoading(false));
  }, [open, driverId, periodKey, token]);

  if (!open) return null;

  return (
    <div style={historyOverlayStyle}>
      <div style={historyBackdropStyle} onClick={onClose} />
      <div style={{ position: "relative", width: "100%", maxWidth: "760px", maxHeight: "80vh", overflow: "hidden" }}>
        <Card padding="20px" style={{ display: "flex", flexDirection: "column", gap: "14px", maxHeight: "80vh" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h3 style={{ margin: 0, fontSize: "16px", fontWeight: 700, color: "var(--text-primary)" }}>Historial de ajustes</h3>
            <Button variant="secondary" onClick={onClose}>Cerrar</Button>
          </div>
          {loading && <div style={stateStyle}>Cargando…</div>}
          {!loading && error && <div style={{ ...stateStyle, color: "var(--danger, #d1242f)" }}>{error}</div>}
          {!loading && !error && history && history.length === 0 && <div style={stateStyle}>Todavía no hay ajustes registrados para esta semana.</div>}
          {!loading && !error && history && history.length > 0 && (
            <div style={{ ...tableWrapStyle, overflowY: "auto" }}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>Fecha/Hora</th>
                    <th style={thStyle}>Usuario</th>
                    <th style={thStyle}>Campo</th>
                    <th style={thStyle}>Día</th>
                    <th style={thStyle}>Valor anterior</th>
                    <th style={thStyle}>Valor nuevo</th>
                    <th style={thStyle}>Motivo</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((h) => (
                    <tr key={h.id}>
                      <td style={tdStyle}>{formatHistoryDate(h.createdAt)}</td>
                      <td style={tdStyle}>{h.createdBy.name || h.createdBy.username || "—"}</td>
                      <td style={tdStyle}>{ADJUSTMENT_FIELD_LABEL[h.field] || h.field}</td>
                      <td style={tdStyle}>{h.scopeDate || "Semana completa"}</td>
                      <td style={tdStyle}>{h.previousValue || "—"}</td>
                      <td style={tdStyle}><strong>{h.newValue}</strong></td>
                      <td style={{ ...tdStyle, whiteSpace: "normal", maxWidth: "220px" }}>{h.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

// Chip clickeable Pendientes (ver auditoría "Alejo 30/09" punto 7 — mismo
// componente que ClienteDetailModal.tsx, duplicado a propósito siguiendo la
// convención de este archivo de no compartir helpers de presentación entre
// Store/Driver). `count` ya viene calculado con pricing_status !== "PRICED"
// — este componente nunca decide qué es "pendiente", solo lo muestra.
function PendientesChip({ count, active, onToggle }: { count: number; active: boolean; onToggle: () => void }) {
  if (count === 0) {
    return <span style={{ ...badgeStyle("#2f8f5b"), opacity: 0.75 }}>🟢 Sin pendientes</span>;
  }
  return (
    <button
      type="button"
      onClick={onToggle}
      style={{
        ...badgeStyle("#c99a1f"),
        cursor: "pointer",
        background: active ? "rgba(201, 154, 31, 0.22)" : "transparent",
        fontFamily: "inherit",
      }}
      title={active ? "Mostrando solo pendientes — click para ver todos" : "Mostrar solo pendientes"}
    >
      🟠 {count} pendiente{count === 1 ? "" : "s"}{active ? " · filtrando" : ""}
    </button>
  );
}

export default function DriverDetailModal({ open, driverId, driverName, token, onClose, periodKey = null, onOverrideChanged }: Props) {
  const [data, setData] = useState<DriverDetailResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [editing, setEditing] = useState<DeliveryDetail | null>(null);
  // Modal sobre modal (ver "LYNX — FASE 3: Modal reutilizable"): abre
  // EnvioLegacyDetailModal (General/Historial) sin tocar ni perder el
  // tab de día activo/search/pendientesOnly de este modal padre.
  const [historyTarget, setHistoryTarget] = useState<number | string | null>(null);
  const [search, setSearch] = useState("");
  // Filtro Pendientes (ver auditoría "Alejo 30/09" punto 7, mismo mecanismo
  // que ClienteDetailModal.tsx): acá la lista visible es siempre la de UN
  // día (activeDay.deliveries, no toda la semana), así que el conteo de
  // pendientes se hace sobre ese mismo alcance — mismo predicado exacto
  // (pricing_status !== "PRICED") que ya usa summary.unresolvedDeliveries a
  // nivel semana, nunca una definición nueva. Se resetea al cambiar de
  // pestaña de día para no dejar un filtro "pegado" mostrando 0 resultados
  // en un día distinto.
  const [pendientesOnly, setPendientesOnly] = useState(false);
  const [tab, setTab] = useState<string>("RESUMEN"); // "RESUMEN" | dateKey
  const periodSuffix = periodKey ? `?period=${encodeURIComponent(periodKey)}` : "";
  const readOnly = data?.period?.readOnly === true;

  // Settlement Adjustments (ver informe "LYNX — Ajustes manuales sobre
  // semanas cerradas"): Vehículo es de alcance semana completa (se edita en
  // la pestaña Resumen); Premio Elite es de alcance por día (se edita
  // dentro de cada pestaña de día) — confirmado con el usuario.
  const [vehiculoValue, setVehiculoValue] = useState("");
  const [vehiculoReason, setVehiculoReason] = useState("");
  const [savingVehiculo, setSavingVehiculo] = useState(false);
  const [vehiculoError, setVehiculoError] = useState<string | null>(null);
  const [eliteDirection, setEliteDirection] = useState<"AUTOMATICO" | "GANADO" | "PERDIDO">("AUTOMATICO");
  const [eliteReason, setEliteReason] = useState("");
  const [savingElite, setSavingElite] = useState(false);
  const [eliteError, setEliteError] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);

  const loadDetail = React.useCallback(() => {
    if (driverId === null || !token) return;
    setLoading(true);
    setError(null);
    return authFetch(`/v1/liquidaciones/choferes/${driverId}${periodSuffix}`, { token })
      .then(async (res) => {
        const payload = await res.json().catch(() => ({}));
        if (!res.ok || payload?.ok === false) throw new Error(payload?.error || "Error al cargar el detalle del chofer");
        setData(payload);
      })
      .catch((e: any) => {
        setError(e.message);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [driverId, token, periodSuffix]);

  useEffect(() => {
    if (!open || driverId === null || !token) return;
    setData(null);
    setSearch("");
    setPendientesOnly(false);
    setTab("RESUMEN");
    loadDetail();
  }, [open, driverId, token, loadDetail]);

  const activeDay = useMemo(() => (data ? data.days.find((d) => d.dateKey === tab) || null : null), [data, tab]);

  useEffect(() => {
    setPendientesOnly(false);
  }, [tab]);

  // Pendientes del día activo (mismo predicado que el backend, ver
  // comentario de pendientesOnly arriba) — no hay un campo precomputado
  // por día en DriverDay/DayTotals, así que se cuenta acá igual que el
  // propio filtro de abajo, nunca con un criterio distinto.
  const activeDayUnresolved = useMemo(
    () => (activeDay ? activeDay.deliveries.filter((d) => d.pricing_status !== "PRICED").length : 0),
    [activeDay]
  );

  // Buscador dentro del día activo (ver auditoría final UX): tracking,
  // destinatario, dirección, CP y Store — client-side, mismo criterio de
  // siempre. Localidad NO se agrega acá a propósito: no está disponible en
  // el pipeline de Driver Daily Performance/driver_daily_snapshot (ver
  // auditoría Fase 1.7 — agregarla requeriría tocar ese módulo y, para días
  // ya congelados, una migración de columna).
  const filteredDeliveries = useMemo(() => {
    if (!activeDay) return [];
    const q = search.trim().toLowerCase();
    let base = activeDay.deliveries;
    if (pendientesOnly) base = base.filter((d) => d.pricing_status !== "PRICED");
    if (!q) return sortByOriginBucket(base);
    return sortByOriginBucket(
      base.filter(
        (d) =>
          (d.tracking || "").toLowerCase().includes(q) ||
          (d.destinatario || "").toLowerCase().includes(q) ||
          (d.direccion || "").toLowerCase().includes(q) ||
          (d.cp || "").toLowerCase().includes(q) ||
          (d.store_name || "").toLowerCase().includes(q)
      )
    );
  }, [activeDay, search, pendientesOnly]);

  // Precarga el ajuste de Vehículo vigente (si hay uno) cada vez que llega
  // un detalle nuevo — así reabrir el modal, o guardar un ajuste, siempre
  // muestra el estado real, nunca un formulario vacío pisando lo vigente.
  useEffect(() => {
    setVehiculoValue(data?.adjustments?.vehiculo?.value || data?.driver?.tariff?.name || "");
    setVehiculoReason("");
    setVehiculoError(null);
  }, [data]);

  // El ajuste de Premio Elite es por día — se reinicia al cambiar de
  // pestaña, precargando lo vigente de ESE día puntual si lo hay.
  useEffect(() => {
    const current = activeDay ? data?.adjustments?.eliteByDay?.[activeDay.dateKey] : null;
    setEliteDirection(current?.direction || "AUTOMATICO");
    setEliteReason("");
    setEliteError(null);
  }, [activeDay, data]);

  if (!open) return null;

  const handleDownload = async () => {
    if (driverId === null) return;
    setDownloading(true);
    try {
      await downloadAuthenticatedFile(
        `/v1/liquidaciones/choferes/${driverId}/export${periodSuffix}`,
        token,
        `liquidacion_${driverName || "chofer"}.xlsx`
      );
    } catch (e: any) {
      alert(e.message || "No se pudo descargar el Excel del chofer.");
    } finally {
      setDownloading(false);
    }
  };

  // Settlement Adjustments (ver informe "LYNX — Ajustes manuales sobre
  // semanas cerradas"): cada guardado es un ajuste NUEVO (append-only, el
  // backend nunca pisa el anterior) — la respuesta ya trae Snapshot +
  // Ajustes recalculado, se aplica directo con setData (mismo patrón que
  // OverrideEditModal.onSaved).
  const saveAdjustment = async (body: Record<string, unknown>) => {
    const res = await authFetch(`/v1/liquidaciones/choferes/${driverId}/adjustments`, { method: "PUT", token, json: true, body: JSON.stringify(body) });
    const payload = await res.json().catch(() => ({}));
    if (!res.ok || payload?.ok === false) throw new Error(payload?.error || "No se pudo guardar el ajuste");
    return payload;
  };

  const handleSaveVehiculo = async () => {
    if (!data || !vehiculoValue) return;
    if (!vehiculoReason.trim()) {
      setVehiculoError("El motivo es obligatorio");
      return;
    }
    setSavingVehiculo(true);
    setVehiculoError(null);
    try {
      const payload = await saveAdjustment({ period: data.period.key, field: "VEHICULO", new_value: vehiculoValue, reason: vehiculoReason.trim() });
      setData(payload);
      onOverrideChanged?.();
    } catch (e: any) {
      setVehiculoError(e.message || "No se pudo guardar el ajuste");
    } finally {
      setSavingVehiculo(false);
    }
  };

  const handleSaveElite = async () => {
    if (!data || !activeDay) return;
    if (!eliteReason.trim()) {
      setEliteError("El motivo es obligatorio");
      return;
    }
    setSavingElite(true);
    setEliteError(null);
    try {
      const payload = await saveAdjustment({
        period: data.period.key,
        field: "ELITE_OVERRIDE",
        scope_date: activeDay.dateKey,
        new_value: eliteDirection,
        reason: eliteReason.trim(),
      });
      setData(payload);
      onOverrideChanged?.();
    } catch (e: any) {
      setEliteError(e.message || "No se pudo guardar el ajuste");
    } finally {
      setSavingElite(false);
    }
  };

  // Acciones rápidas (Caso 5 del informe "LYNX — Ajustes finales de
  // Liquidaciones + Auditoría de Performance"): "Aplicar Premio a todo el
  // día" / "Anular Premio de todo el día" — mismo endpoint/ajuste
  // ELITE_OVERRIDE de siempre, sin editar envío por envío. El motivo
  // (obligatorio) se pide con un prompt en vez del formulario detallado,
  // para que sea un click real — el control fino de abajo sigue
  // disponible para cuando haga falta más detalle.
  const handleQuickEliteAction = async (direction: "GANADO" | "PERDIDO") => {
    if (!data || !activeDay) return;
    const label = direction === "GANADO" ? "aplicar el Premio a todo el día" : "anular el Premio de todo el día";
    const reason = window.prompt(`Motivo para ${label} (${activeDay.dayLabel}):`);
    if (reason === null) return; // cancelado
    if (!reason.trim()) {
      alert("El motivo es obligatorio.");
      return;
    }
    setSavingElite(true);
    setEliteError(null);
    try {
      const payload = await saveAdjustment({ period: data.period.key, field: "ELITE_OVERRIDE", scope_date: activeDay.dateKey, new_value: direction, reason: reason.trim() });
      setData(payload);
      onOverrideChanged?.();
    } catch (e: any) {
      alert(e.message || "No se pudo guardar el ajuste");
    } finally {
      setSavingElite(false);
    }
  };

  return (
    <div style={overlayStyle}>
      <div style={backdropStyle} onClick={onClose} />
      <div style={shellStyle}>
        <Card padding="0" style={{ display: "flex", flexDirection: "column" }}>
          <div style={headerStyle}>
            <div>
              <h2 style={titleStyle}>{data?.driver?.name || driverName || "Chofer"}</h2>
              {data && (
                <p style={subtitleStyle}>
                  {data.period.fromDisplay} → {data.period.toDisplay} · {data.summary.deliveries} envío(s) · Total {money(data.summary.totalFinal)}
                  {readOnly && <span style={badgeStyle("#c99a1f")}> Semana cerrada · Solo lectura</span>}
                </p>
              )}
            </div>
            <div style={{ display: "flex", gap: "8px" }}>
              <Button variant="secondary" onClick={handleDownload} disabled={downloading || !data}>
                {downloading ? "Generando…" : "Descargar Excel"}
              </Button>
              <Button variant="secondary" onClick={onClose}>Cerrar</Button>
            </div>
          </div>

          <div style={bodyStyle}>
            {loading && <div style={stateStyle}>Cargando…</div>}
            {!loading && error && <div style={{ ...stateStyle, color: "var(--danger, #d1242f)" }}>{error}</div>}

            {!loading && !error && data && (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "10px", ...summaryCardStyle }}>
                <div>
                  <div style={summaryLabelStyle}>Username LightData</div>
                  <div style={summaryValueStyle}>{data.driver.lightdataUsername || "—"}</div>
                </div>
                <div>
                  <div style={summaryLabelStyle}>Vehículo</div>
                  <div style={summaryValueStyle}>{data.driver.vehiculo || "Sin informar"}</div>
                </div>
                <div>
                  <div style={summaryLabelStyle}>Tarifa actual</div>
                  <div style={summaryValueStyle}>{data.driver.tariff?.name || "Sin tarifa"}</div>
                </div>
                <div>
                  <div style={summaryLabelStyle}>Estado</div>
                  <div style={{ marginTop: "2px" }}>
                    {data.driver.configStatus && (
                      <span style={{ display: "inline-block", padding: "2px 9px", borderRadius: "999px", fontSize: "11px", fontWeight: 700, border: `1px solid ${CONFIG_BADGE[data.driver.configStatus].color}`, color: CONFIG_BADGE[data.driver.configStatus].color }}>
                        {CONFIG_BADGE[data.driver.configStatus].label}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}
            {!loading && !error && data?.adjustments?.hasAny && (
              <div style={adjustmentsBannerStyle}>
                <span>⚠ Esta liquidación posee ajustes manuales.</span>
                <Button variant="secondary" onClick={() => setHistoryOpen(true)}>Ver historial de ajustes</Button>
              </div>
            )}
            {/* Liquidaciones es solo lectura para la tarifa a propósito (Sprint
                Operativo, Parte 1): cambiarla es propenso a error y no es el
                lugar — se administra desde Usuarios → Editar Chofer. Los
                Ajustes Manuales (más abajo) son otra cosa: correcciones
                auditadas sobre UNA semana ya cerrada puntual, nunca tocan
                driver_data.tarifa_id. */}

            {!loading && !error && data && (
              <div style={tabBarStyle}>
                <button type="button" style={tabBtnStyle(tab === "RESUMEN")} onClick={() => setTab("RESUMEN")}>
                  Resumen
                </button>
                {data.days.map((d) => (
                  <button key={d.dateKey} type="button" style={tabBtnStyle(tab === d.dateKey)} onClick={() => setTab(d.dateKey)}>
                    {shortDayLabel(d.dayLabel)}
                    {!d.closed && <span style={{ marginLeft: "4px", opacity: 0.7 }}>•</span>}
                  </button>
                ))}
              </div>
            )}

            {!loading && !error && data && tab === "RESUMEN" && (
              <>
                <div className="liq-wide-table-wrap" style={wideTableWrapStyle}>
                  <table style={tableStyle}>
                    <thead>
                      <tr>
                        <th style={thStyle}>Día</th>
                        <th style={thStyle}>Envíos</th>
                        <th style={thStyle}>Base</th>
                        <th style={thStyle}>Premios</th>
                        <th style={thStyle}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.days.map((d) => (
                        <tr key={d.dateKey} style={{ cursor: "pointer" }} onClick={() => setTab(d.dateKey)}>
                          <td style={tdStyle}>
                            {d.dayLabel}
                            {!d.closed && <span style={badgeStyle("#1f7ac9")}>En curso</span>}
                          </td>
                          <td style={tdStyle}>{d.totals.deliveries}</td>
                          <td style={tdStyle}>{money(d.totals.base)}</td>
                          <td style={tdStyle}>{money(d.totals.premios)}</td>
                          <td style={tdStyle}><strong>{money(d.totals.total)}</strong></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "10px" }}>
                  <div style={summaryCardStyle}>
                    <div style={summaryLabelStyle}>Total Base</div>
                    <div style={{ fontSize: "18px", fontWeight: 700 }}>{money(data.summary.totalReparto)}</div>
                  </div>
                  <div style={summaryCardStyle}>
                    <div style={summaryLabelStyle}>Total Premios</div>
                    <div style={{ fontSize: "18px", fontWeight: 700, color: "var(--success, #2ea043)" }}>{money(data.summary.premioElite)}</div>
                  </div>
                  <div style={summaryCardStyle}>
                    <div style={summaryLabelStyle}>Total General</div>
                    <div style={{ fontSize: "18px", fontWeight: 700 }}>{money(data.summary.totalFinal)}</div>
                  </div>
                </div>

                {readOnly && data.driver.availableTariffs && (
                  <div style={adjustmentsSectionStyle}>
                    <p style={adjustmentsTitleStyle}>🔒 Semana Cerrada — Ajustes Manuales</p>
                    <div>
                      <p style={{ margin: "0 0 8px 0", fontSize: "13px", fontWeight: 700, color: "var(--text-primary)" }}>Vehículo (toda la semana)</p>
                      <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                        {data.driver.availableTariffs.map((t) => (
                          <OptionCard key={t.id} icon="🚚" label={t.name} active={vehiculoValue === t.name} onClick={() => setVehiculoValue(t.name)} />
                        ))}
                      </div>
                    </div>
                    {data.adjustments?.vehiculo && (
                      <div style={currentAdjustmentNoteStyle}>
                        Ajuste vigente: <strong>{data.adjustments.vehiculo.value}</strong> — {data.adjustments.vehiculo.reason}
                      </div>
                    )}
                    <Input label="Motivo (obligatorio)" value={vehiculoReason} onChange={(e) => setVehiculoReason(e.target.value)} placeholder="Ej: El chofer trabajó con otro vehículo esa semana" />
                    {vehiculoError && <div style={{ color: "var(--danger, #d1242f)", fontSize: "13px" }}>{vehiculoError}</div>}
                    <div>
                      <Button onClick={handleSaveVehiculo} disabled={savingVehiculo || !vehiculoValue}>
                        {savingVehiculo ? "Guardando…" : "Guardar Ajuste"}
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}

            {!loading && !error && data && activeDay && (
              <>
                <p style={{ margin: 0, fontSize: "13px", color: "var(--text-secondary)" }}>
                  {activeDay.dayLabel}
                  {!activeDay.closed && " — todavía en curso, el Premio Elite no es definitivo hasta que cierre el día."}
                </p>
                {activeDay.deliveries.length > 0 && (
                  <DayEliteCounters deliveries={activeDay.deliveries} dayEliteReason={activeDay.eliteReason} />
                )}
                {activeDay.deliveries.length === 0 ? (
                  <div style={stateStyle}>Sin envíos para este chofer ese día.</div>
                ) : (
                  <>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                      <div style={searchFieldStyle}>
                        <Input
                          placeholder="Buscar por tracking, destinatario, dirección, CP o Tienda..."
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                        />
                      </div>
                      <PendientesChip
                        count={activeDayUnresolved}
                        active={pendientesOnly}
                        onToggle={() => setPendientesOnly((v) => !v)}
                      />
                    </div>
                    {filteredDeliveries.length === 0 ? (
                      <div style={stateStyle}>Ningún envío coincide con la búsqueda.</div>
                    ) : (
                      <div className="liq-wide-table-wrap" style={wideTableWrapStyle}>
                        <table style={tableStyle}>
                          <thead>
                            <tr>
                              <th style={thStyle}>Tracking</th>
                              <th style={thStyle}>Destinatario</th>
                              <th style={thStyle}>Tienda</th>
                              <th style={thStyle}>Dirección</th>
                              <th style={thStyle}>CP</th>
                              <th style={thStyle}>Zona</th>
                              <th style={thStyle} title="¿Este envío pertenece a la ventana Same Day del driver/día que se está mostrando? No es solo una comparación de fechas — un envío reasignado después del cierre muestra 'No' en la fila económica del nuevo driver.">
                                Same Day
                              </th>
                              <th style={thStyle}>Base</th>
                              <th style={thStyle}>Premio</th>
                              <th style={thStyle}>Total</th>
                              <th style={thStyle}>Estado Premio</th>
                              <th style={thStyle}></th>
                              {!readOnly && <th style={thStyle}></th>}
                            </tr>
                          </thead>
                          <tbody>
                            {filteredDeliveries.map((d) => {
                              // is_elite_evidence_only (ver auditoría "LYNX — Reasignación Same Day"):
                              // Base/Premio/Total en null/0 son INTENCIONALES acá, nunca "pendiente" —
                              // es evidencia histórica, no un envío sin precio todavía.
                              const isEvidence = d.is_elite_evidence_only === true;
                              const pending = !isEvidence && d.final_price === null;
                              const isReassignedEconomic = d.same_day === false;
                              // TURBO/NON_FLEX (ver "LYNX — Cierre definitivo Elite/Snapshot 06/10",
                              // punto 4/6): ninguno de los dos participa de Premio Elite — tienen su
                              // propia etiqueta explícita más abajo (TURBO_LABEL/nonFlexLabel), nunca
                              // "No elegible" ni el badge genérico de ELITE_STATUS_LABEL.
                              const eliteBadge = d.elite_status && d.elite_status !== "TURBO" && d.elite_status !== "NON_FLEX" ? ELITE_STATUS_LABEL[d.elite_status] : null;
                              // Ver auditoría "LYNX — Aura Flex / Elite individual vs pérdida
                              // global": un envío LOST puede ser (a) el/los causante(s) real(es)
                              // (elite_is_causante=true) o (b) individualmente exitoso, sin premio
                              // solo porque OTRO envío hizo perder el día — antes ambos casos se
                              // veían idénticos ("Perdido" + el mismo motivo ajeno). Ahora se
                              // distinguen: (b) muestra su propio hecho positivo + aviso de que el
                              // premio se anuló por el día, (a) muestra 🔴 Causante + su motivo real.
                              const isCausante = d.elite_is_causante === true;
                              const isSuccessfulButDayLost = d.elite_status === "LOST" && !isCausante;
                              const ownCategory = categorizeEliteReason(d.elite_reason);
                              return (
                                <tr
                                  key={String(d.lightdata_id)}
                                  style={{ cursor: "pointer", ...(isEvidence ? { opacity: 0.68 } : null) }}
                                  title={
                                    isEvidence
                                      ? "Evidencia histórica — no es un envío pagable para este chofer, explica su Premio Elite de este día"
                                      : pending
                                      ? d.pricing_reason || undefined
                                      : d.elite_reason || d.override_observation || undefined
                                  }
                                  onClick={() => setHistoryTarget(d.lightdata_id)}
                                >
                                  <td style={tdStyle}>{d.tracking || "—"}</td>
                                  <td style={tdStyle}>{d.destinatario || "Sin informar"}</td>
                                  <td style={tdStyle}>{d.store_name || "—"}</td>
                                  <td style={tdStyle}>{d.direccion || "Sin informar"}</td>
                                  <td style={tdStyle}>{d.cp || "—"}</td>
                                  <td style={tdStyle}>{d.tier || "—"}</td>
                                  <td style={tdStyle}>{d.same_day === false ? "No" : "Sí"}</td>
                                  <td style={tdStyle}>
                                    {isEvidence ? "—" : pending ? <span style={{ color: "var(--danger, #d1242f)" }}>Pendiente</span> : money(d.base_price ?? null)}
                                  </td>
                                  <td style={tdStyle}>{isEvidence || pending ? "—" : money(d.elite_bonus || null)}</td>
                                  <td style={tdStyle}>{isEvidence ? "—" : pending ? "—" : <strong>{money(d.final_price)}</strong>}</td>
                                  <td style={tdStyle}>
                                    {isEvidence && (
                                      <span style={badgeStyle("var(--text-secondary)")} title="No es un pago vigente — explica el Premio Elite de este driver ese día">
                                        📋 Evidencia (no pagable)
                                      </span>
                                    )}
                                    {!isEvidence && isReassignedEconomic && (
                                      <span style={badgeStyle("var(--text-secondary)")} title="Este envío ya jugó su ventana Elite bajo otro driver — Same Day = No">
                                        Same Day: No
                                      </span>
                                    )}
                                    {!isEvidence && !isReassignedEconomic && d.elite_status === "TURBO" && (
                                      <span style={badgeStyle(TURBO_LABEL.color)}>{TURBO_LABEL.icon} {TURBO_LABEL.label}</span>
                                    )}
                                    {!isEvidence && !isReassignedEconomic && d.elite_status === "NON_FLEX" && (() => {
                                      const nonFlex = nonFlexLabel(d.origen);
                                      return <span style={badgeStyle(nonFlex.color)}>{nonFlex.icon} {nonFlex.label}</span>;
                                    })()}
                                    {/* NOT_ELIGIBLE (ver "LYNX — FASE 4: Elite visual"): el motor
                                        YA devuelve esto para candidatos que no llegaron a competir
                                        (ej. entregado 21-23, Nadie/Reprogramado pre-21, UNKNOWN,
                                        cancelado, rechazado, sin hora válida -- eliteDecision.service.js).
                                        Antes NO se mostraba nada acá porque ELITE_STATUS_LABEL no
                                        tenía esta clave -- la fila quedaba en blanco. Reutiliza
                                        categorizeEliteReason, igual que las demás categorías, sin
                                        tocar ninguna decisión del motor. */}
                                    {!isEvidence && !isReassignedEconomic && d.elite_status === "NOT_ELIGIBLE" && (
                                      <span style={badgeStyle((ownCategory || { color: "var(--text-secondary)" }).color)} title={d.elite_reason || undefined}>
                                        {ownCategory ? `${ownCategory.icon} ${ownCategory.label}` : "Sin premio Elite"}
                                      </span>
                                    )}
                                    {!isEvidence && !isReassignedEconomic && eliteBadge && isSuccessfulButDayLost && (
                                      <div>
                                        <span style={badgeStyle("var(--success, #2ea043)")} title={d.elite_reason || undefined}>
                                          {ownCategory ? `${ownCategory.icon} ${ownCategory.label}` : "🟢 Ganado (individual)"}
                                        </span>
                                        <div style={{ fontSize: "11px", color: "var(--text-secondary)", marginTop: "3px", whiteSpace: "normal" }}>
                                          Premio $0 — pérdida global del día
                                        </div>
                                      </div>
                                    )}
                                    {!isEvidence && !isReassignedEconomic && eliteBadge && isCausante && (
                                      <div>
                                        <span style={badgeStyle("var(--danger, #d1242f)")} title={d.elite_reason || undefined}>
                                          🔴 Causante
                                        </span>
                                        {ownCategory && (
                                          <div style={{ fontSize: "11px", color: ownCategory.color, marginTop: "3px", whiteSpace: "normal" }}>
                                            {ownCategory.icon} {ownCategory.label}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                    {!isEvidence && !isReassignedEconomic && eliteBadge && !isSuccessfulButDayLost && !isCausante && (
                                      <div>
                                        <span style={badgeStyle((ownCategory || eliteBadge).color)} title={d.elite_reason || undefined}>
                                          {ownCategory ? `${ownCategory.icon} ${ownCategory.label}` : eliteBadge.label}
                                        </span>
                                      </div>
                                    )}
                                    {isEvidence && isCausante && (
                                      <div style={{ fontSize: "11px", color: "var(--danger, #d1242f)", marginTop: "3px", whiteSpace: "normal" }}>
                                        🔴 Causante de la pérdida global de este día
                                      </div>
                                    )}
                                  </td>
                                  <td style={tdStyle}>
                                    <ExceptionBadges d={d} />
                                  </td>
                                  {!readOnly && (
                                    <td style={tdStyle}>
                                      <button
                                        type="button"
                                        style={isEvidence ? { ...editButtonStyle, opacity: 0.4, cursor: "not-allowed" } : editButtonStyle}
                                        title={isEvidence ? "Evidencia histórica — no editable (la edición vigente vive en la liquidación económica actual)" : "Editar precio / Turbo / Doble"}
                                        disabled={isEvidence}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (!isEvidence) setEditing(d);
                                        }}
                                      >
                                        ✎
                                      </button>
                                    </td>
                                  )}
                                </tr>
                              );
                            })}
                          </tbody>
                          <tfoot>
                            <tr>
                              <td style={tdStyle} colSpan={7}><strong>Totales del día</strong></td>
                              <td style={tdStyle}><strong>{money(activeDay.totals.base)}</strong></td>
                              <td style={tdStyle}><strong>{money(activeDay.totals.premios)}</strong></td>
                              <td style={tdStyle}><strong>{money(activeDay.totals.total)}</strong></td>
                              <td style={tdStyle} colSpan={readOnly ? 2 : 3}></td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    )}
                  </>
                )}
                {activeDay.eliteStatus && (
                  <div
                    style={{
                      ...summaryCardStyle,
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "center",
                      gap: "16px",
                      borderColor: activeDay.eliteStatus === "GANADO" ? "var(--success, #2ea043)" : "var(--danger, #d1242f)",
                    }}
                  >
                    <div>
                      <div style={summaryLabelStyle}>Premio Elite</div>
                      <div style={{ fontSize: "16px", fontWeight: 700, color: activeDay.eliteStatus === "GANADO" ? "var(--success, #2ea043)" : "var(--danger, #d1242f)" }}>
                        {activeDay.eliteStatus === "GANADO" ? "✅ GANADO" : "❌ PERDIDO"}
                      </div>
                    </div>
                    {activeDay.eliteReason && (
                      <div>
                        <div style={summaryLabelStyle}>Motivo (del día completo)</div>
                        <div style={summaryValueStyle}>{activeDay.eliteReason}</div>
                      </div>
                    )}
                    {activeDay.eliteCausantes && activeDay.eliteCausantes.length > 0 && (
                      <div>
                        <div style={summaryLabelStyle}>
                          {activeDay.eliteCausantes.length === 1 ? "Envío causante" : `Envíos causantes (${activeDay.eliteCausantes.length})`}
                        </div>
                        <div style={{ ...summaryValueStyle, display: "flex", flexDirection: "column", gap: "6px" }}>
                          {activeDay.eliteCausantes.map((c) => {
                            const hora = argentinaHour(c.fecha_estado);
                            return (
                              <div key={String(c.lightdata_id)} style={{ fontSize: "13px" }}>
                                <div>
                                  {c.tracking || c.lightdata_id} — {c.store_name || "Tienda sin informar"}
                                </div>
                                <div style={{ color: "var(--text-secondary)", fontSize: "12px" }}>
                                  {c.estado || "—"}
                                  {hora ? ` ${hora}` : ""} — {c.reason || "—"}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    <div>
                      <div style={summaryLabelStyle}>Total Base</div>
                      <div style={summaryValueStyle}>{money(activeDay.totals.base)}</div>
                    </div>
                    <div>
                      <div style={summaryLabelStyle}>Total Premios</div>
                      <div style={summaryValueStyle}>{money(activeDay.totals.premios)}</div>
                    </div>
                    <div>
                      <div style={summaryLabelStyle}>Total Día</div>
                      <div style={{ ...summaryValueStyle, fontSize: "16px" }}>{money(activeDay.totals.total)}</div>
                    </div>
                  </div>
                )}

                {readOnly && (
                  <div style={adjustmentsSectionStyle}>
                    <p style={adjustmentsTitleStyle}>🔒 Semana Cerrada — Ajustes Manuales</p>
                    <div>
                      <p style={{ margin: "0 0 8px 0", fontSize: "13px", fontWeight: 700, color: "var(--text-primary)" }}>Acciones rápidas — Premio Elite ({activeDay.dayLabel})</p>
                      <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                        <Button onClick={() => handleQuickEliteAction("GANADO")} disabled={savingElite}>
                          ✅ Aplicar Premio a todo el día
                        </Button>
                        <Button variant="danger" onClick={() => handleQuickEliteAction("PERDIDO")} disabled={savingElite}>
                          ❌ Anular Premio de todo el día
                        </Button>
                      </div>
                    </div>
                    <div>
                      <p style={{ margin: "0 0 8px 0", fontSize: "13px", fontWeight: 700, color: "var(--text-primary)" }}>Control fino ({activeDay.dayLabel})</p>
                      <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                        <OptionCard icon="⚙️" label="Automático" active={eliteDirection === "AUTOMATICO"} onClick={() => setEliteDirection("AUTOMATICO")} />
                        <OptionCard icon="✅" label="Forzar Ganado" active={eliteDirection === "GANADO"} onClick={() => setEliteDirection("GANADO")} />
                        <OptionCard icon="❌" label="Forzar Perdido" active={eliteDirection === "PERDIDO"} onClick={() => setEliteDirection("PERDIDO")} />
                      </div>
                    </div>
                    {activeDay && data?.adjustments?.eliteByDay?.[activeDay.dateKey] && (
                      <div style={currentAdjustmentNoteStyle}>
                        Ajuste vigente: <strong>{data.adjustments.eliteByDay[activeDay.dateKey].direction}</strong> — {data.adjustments.eliteByDay[activeDay.dateKey].reason}
                      </div>
                    )}
                    <Input label="Motivo (obligatorio)" value={eliteReason} onChange={(e) => setEliteReason(e.target.value)} placeholder="Ej: Chofer configurado incorrectamente" />
                    {eliteError && <div style={{ color: "var(--danger, #d1242f)", fontSize: "13px" }}>{eliteError}</div>}
                    <div>
                      <Button onClick={handleSaveElite} disabled={savingElite}>
                        {savingElite ? "Guardando…" : "Guardar Ajuste"}
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </Card>
      </div>

      <OverrideEditModal
        open={editing !== null}
        entityLabel={`Chofer: ${data?.driver?.name || driverName || "—"}`}
        token={token}
        delivery={editing}
        overrideUrl={`/v1/liquidaciones/choferes/${driverId}/envios/${editing?.lightdata_id}/override`}
        onClose={() => setEditing(null)}
        onSaved={(detail) => {
          setData(detail);
          setEditing(null);
          onOverrideChanged?.();
        }}
      />

      <AdjustmentHistoryModal
        open={historyOpen}
        driverId={driverId}
        periodKey={data?.period?.key ?? null}
        token={token}
        onClose={() => setHistoryOpen(false)}
      />

      <EnvioLegacyDetailModal
        open={historyTarget !== null}
        lightdataId={historyTarget}
        token={token}
        onClose={() => setHistoryTarget(null)}
      />

      <style>
        {`
          .liq-wide-table-wrap {
            overflow-x: visible;
          }

          @media (max-width: 1180px) {
            .liq-wide-table-wrap {
              overflow-x: auto;
            }
          }
        `}
      </style>
    </div>
  );
}
